import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  LinkSlashIcon,
} from "@heroicons/react/24/outline";
import { PMButton } from "../../utils/PMButton";
import {
  MICROSOFT_OAUTH_INTENT_KEY,
  MICROSOFT_OAUTH_RETURN_PATH_KEY,
} from "../../../utils/microsoftOAuth";
import {
  getMicrosoftAuthorizeUrl,
  postMicrosoftSendTestMail,
} from "../../../api/microsoft";
import { MicrosoftIntegrationExplainer } from "./MicrosoftIntegrationExplainer";
import { getAccessToken } from "../../../slices/session";

/**
 * @param {{
 *   variant: 'company' | 'user',
 *   titleKey: string,
 *   fetchStatus: () => Promise<any>,
 *   onDisconnect: () => Promise<any>,
 *   intent: 'company_inbox' | 'user_mail',
 *   onStatusChange?: (status: any) => void,
 * }} props
 */
export function MicrosoftConnectPanel({
  variant,
  titleKey,
  fetchStatus,
  onDisconnect,
  intent,
  onStatusChange,
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetchStatus()
      .then((data) => {
        setStatus(data);
        onStatusChange?.(data);
      })
      .catch(() => {
        const fallback = { connected: false, apiMissing: true };
        setStatus(fallback);
        onStatusChange?.(fallback);
      })
      .finally(() => setLoading(false));
  }, [fetchStatus, onStatusChange]);

  useEffect(() => {
    load();
  }, [load]);

  const startConnect = async () => {
    const token = getAccessToken();
    if ((intent === "company_inbox" || intent === "user_mail") && !token) {
      sessionStorage.setItem(
        "promang_ms_oauth_after_login",
        JSON.stringify({ intent, returnPath: window.location.pathname })
      );
      toast.error(t("microsoft.toast.login_required"));
      return;
    }

    try {
      const data = await getMicrosoftAuthorizeUrl(intent);
      const url = data?.url;
      if (!url) {
        toast.error(t("microsoft.toast.start_failed"));
        return;
      }
      sessionStorage.setItem(MICROSOFT_OAUTH_INTENT_KEY, intent);
      sessionStorage.setItem(
        MICROSOFT_OAUTH_RETURN_PATH_KEY,
        window.location.pathname
      );
      window.location.assign(url);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 503 || status === 422) {
        toast.error(
          e?.response?.data?.errors?.[0] || t("microsoft.toast.missing_env")
        );
      } else {
        toast.error(t("microsoft.toast.start_failed"));
      }
    }
  };

  const handleSendTestMail = () => {
    if (variant !== "user" || !status?.can_send_mail) return;
    setSendingTest(true);
    postMicrosoftSendTestMail()
      .then((data) => {
        toast.success(data?.message || t("microsoft.test_mail.success"));
        load();
      })
      .catch((e) => {
        const msg =
          e?.response?.data?.errors?.[0] || t("microsoft.test_mail.failed");
        toast.error(msg);
      })
      .finally(() => setSendingTest(false));
  };

  const handleDisconnect = () => {
    setBusy(true);
    onDisconnect()
      .then(() => {
        toast.success(t("microsoft.toast.disconnected"));
        load();
      })
      .catch(() => {
        toast.error(t("microsoft.toast.disconnect_failed"));
      })
      .finally(() => setBusy(false));
  };

  const connected = Boolean(status?.connected || status?.email_address);
  const email =
    status?.email_address || status?.mailbox || status?.microsoft_email;

  return (
    <div className="space-y-6">
      <MicrosoftIntegrationExplainer variant={variant} />

      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
        <h2 className="text-lg font-semibold text-gray-900">
          {t(titleKey)}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {variant === "company"
            ? t("microsoft.company.subtitle")
            : t("microsoft.user.subtitle")}
        </p>

        {loading ? (
          <div className="mt-6 flex items-center gap-2 text-sm text-gray-500">
            <ArrowPathIcon className="h-5 w-5 animate-spin" />
            {t("microsoft.status.loading")}
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {status?.apiMissing && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-amber-200">
                {t("microsoft.status.api_pending")}
              </p>
            )}

            {connected ? (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-2">
                    <CheckCircleIcon className="h-6 w-6 shrink-0 text-emerald-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {t("microsoft.status.connected")}
                      </p>
                      {email && (
                        <p className="text-sm text-gray-600">{email}</p>
                      )}
                    </div>
                  </div>
                  <PMButton
                    type="button"
                    variant="outlined"
                    disabled={busy || status?.apiMissing}
                    onClick={handleDisconnect}
                  >
                    <span className="inline-flex items-center gap-2">
                      <LinkSlashIcon className="h-5 w-5" />
                      {t("microsoft.action.disconnect")}
                    </span>
                  </PMButton>
                </div>
                {variant === "user" && status?.can_send_mail && (
                  <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/80 px-3 py-3">
                    <p className="text-xs text-gray-600">
                      {t("microsoft.test_mail.hint")}
                    </p>
                    <PMButton
                      type="button"
                      variant="contained"
                      className="mt-2"
                      disabled={sendingTest || busy || status?.apiMissing}
                      onClick={handleSendTestMail}
                    >
                      {t("microsoft.test_mail.button")}
                    </PMButton>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <PMButton
                  type="button"
                  variant="contained"
                  onClick={startConnect}
                >
                  {t("microsoft.action.connect_microsoft")}
                </PMButton>
                <p className="text-xs text-gray-500">
                  {t("microsoft.action.redirect_hint")}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
